import * as React from 'react'

import { cn } from './utils'

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * `panel` — frosted soft-panel shell (default).
   * `plain` — single-surface card; use when nested inside another Card.
   */
  variant?: 'panel' | 'plain'
  /** Extra classes on the frosted outer shell (`panel` only). */
  wrapperClassName?: string
  /** @deprecated Soft-panel outer already frosts; kept for call-site compat. */
  glass?: boolean
}

/**
 * Shared card — Plenty soft-panel shell (frosted outer + white inner).
 * `className` styles the content surface; `wrapperClassName` the outer shell.
 */
const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      className,
      wrapperClassName,
      variant = 'panel',
      glass: _glass,
      children,
      ...props
    },
    ref
  ) => {
    if (variant === 'plain') {
      return (
        <div
          ref={ref}
          className={cn(
            'card-new flex flex-col gap-2 p-3',
            className
          )}
          {...props}
        >
          {children}
        </div>
      )
    }

    return (
      <div
        ref={ref}
        className={cn('soft-panel', wrapperClassName)}
        {...props}
      >
        <div
          className={cn(
            'card-inner flex flex-col gap-2 p-3 text-card-foreground',
            className
          )}
        >
          {children}
        </div>
      </div>
    )
  }
)
Card.displayName = 'Card'

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col space-y-1.5 p-2', className)}
    {...props}
  />
))
CardHeader.displayName = 'CardHeader'

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn('text-lg  leading-none tracking-tight', className)}
    {...props}
  />
))
CardTitle.displayName = 'CardTitle'

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
))
CardDescription.displayName = 'CardDescription'

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('p-2 pt-0', className)} {...props} />
))
CardContent.displayName = 'CardContent'

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center p-2 pt-0', className)}
    {...props}
  />
))
CardFooter.displayName = 'CardFooter'

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
